import React, { useState } from "react"

import {
	ThemeProvider,
	Dialog,
	DialogContent,
	DialogTitle,
	DialogActions,
	Button,
	IconButton,
	Typography,
	Grid,
	TextField,
	FormControl,
	FormControlLabel,
	Radio,
	RadioGroup,
	Collapse,
	CircularProgress
} from "@material-ui/core"
import {
	WhatsApp as WhatsAppIcon,
	Close as CloseIcon
} from "@material-ui/icons"
import {
	WifiIcon,
	ShieldCheckIcon,
	SparklesIcon,
	CheckIcon
} from "lucide-react"

import theme from "@/styles/theme"
import { colors } from "@/styles/colors"
import { videoLinks } from "@/utils/link"
import { renderComponent, unmountComponent } from "@/utils/node"
import useValidation, { ErrorType } from "@/hooks/useValidation"
import WABAMigrationService from "@/services/WABAMigration"

import useStyles from "@/components/WABAMigrationModal/styles"

type Step = "info" | "form" | "success"

const MODAL_ID = "waba-migration-modal"

type WABAMigrationModalProps = {
	initialName: string
	initialPhone: string
}

type WABAMigrationModalType = {
	open: (props: WABAMigrationModalProps) => void
} & React.FC<WABAMigrationModalProps>

type ObjectiveOption = {
	value: string
	label: string
}

type FormData = {
	name: string
	phone: string
	objective: string
	objectiveOther: string
}

const objectiveOptions: ObjectiveOption[] = [
	{ value: "estabilidade", label: "Estabilidade" },
	{ value: "automacoes", label: "Automações" },
	{ value: "crescimento", label: "Crescimento" },
	{ value: "integracoes", label: "Integrações" },
	{ value: "seguranca_operacional", label: "Segurança operacional" },
	{ value: "outro", label: "Outro" }
]

const WABAMigrationModalContent: React.FC<WABAMigrationModalProps> = (props) => {
	const { initialName, initialPhone } = props
	const classes = useStyles()
	const { validation, addValidation, triggerValidation, clearAllValidations } = useValidation()

	const [step, setStep] = useState<Step>("info")
	const [formData, setFormData] = useState<FormData>({
		name: initialName,
		phone: initialPhone,
		objective: "",
		objectiveOther: ""
	})
	const [loading, setLoading] = useState<boolean>(false)
	const [opened, setOpened] = useState<boolean>(true)

	const handleClose = () => {
		setOpened(false)
		unmountComponent(MODAL_ID)
	}

	const handleGoToForm = () => {
		setStep("form")
	}

	const handleGoBack = () => {
		setStep("info")
		clearAllValidations()
	}

	const handleSubmit = async () => {
		const newValidation: Record<string, string> = {}
		const isOtherSelected = formData.objective === "outro"

		if (!formData.name.trim()) {
			newValidation.name = "Campo obrigatório"
		}

		if (!formData.phone.trim()) {
			newValidation.phone = "Campo obrigatório"
		}

		if (!formData.objective) {
			newValidation.objective = "Campo obrigatório"
		}

		if (isOtherSelected && !formData.objectiveOther.trim()) {
			newValidation.objectiveOther = "Campo obrigatório"
		}

		if (Object.keys(newValidation).length > 0) {
			addValidation(newValidation)
			return
		}

		const effectiveObjective = isOtherSelected ? formData.objectiveOther : formData.objective

		setLoading(true)

		try {
			await WABAMigrationService.submitMigrationRequest({ name: formData.name, phone: formData.phone, objective: effectiveObjective })
			setStep("success")
		} catch (error) {
			triggerValidation(error as ErrorType)
		} finally {
			setLoading(false)
		}
	}

	const renderInfoStep = () => (
		<>
			<IconButton onClick={handleClose} className={classes.closeIcon}>
				<CloseIcon />
			</IconButton>
			<DialogTitle disableTypography>
				<Grid container direction="column" spacing={1}>
					<Grid item>
						<Typography variant="h6" className={classes.title}>
							Evoluir para a API Oficial do WhatsApp
						</Typography>
					</Grid>
					<Grid item>
						<Typography variant="body2" className={classes.subtitle}>
							Migre sua conta Web para a API oficial <strong>sem começar do zero</strong>. Sua estrutura, histórico e configurações são mantidos.
						</Typography>
					</Grid>
				</Grid>
			</DialogTitle>

			<DialogContent>
				<Grid container direction="column" spacing={2}>
					<Grid item>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<Grid container alignItems="flex-start" spacing={2} wrap="nowrap">
									<Grid item>
										<div className={classes.benefitIconBox}>
											<WifiIcon size={20} color={colors.gray[500]} />
										</div>
									</Grid>
									<Grid item xs>
										<Typography variant="body2" className={classes.benefitTitle}>
											Estabilidade contínua
										</Typography>
										<Typography variant="body2" className={classes.subtitle}>
											Conexão na nuvem da Meta, sem quedas nem dependência de QR Code.
										</Typography>
									</Grid>
								</Grid>
							</Grid>

							<Grid item xs={12}>
								<Grid container alignItems="flex-start" spacing={2} wrap="nowrap">
									<Grid item>
										<div className={classes.benefitIconBox}>
											<ShieldCheckIcon size={20} color={colors.gray[500]} />
										</div>
									</Grid>
									<Grid item xs>
										<Typography variant="body2" className={classes.benefitTitle}>
											Mais segurança
										</Typography>
										<Typography variant="body2" className={classes.subtitle}>
											Utilize um número conectado à infraestrutura oficial da Meta mais alinhado às políticas da plataforma.
										</Typography>
									</Grid>
								</Grid>
							</Grid>

							<Grid item xs={12}>
								<Grid container alignItems="flex-start" spacing={2} wrap="nowrap">
									<Grid item>
										<div className={classes.benefitIconBox}>
											<SparklesIcon size={20} color={colors.gray[500]} />
										</div>
									</Grid>
									<Grid item xs>
										<Typography variant="body2" className={classes.benefitTitle}>
											Recursos mais recentes
										</Typography>
										<Typography variant="body2" className={classes.subtitle}>
											Acesso aos novos recursos oficiais lançados pela Meta.
										</Typography>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>

					<Grid item>
						<div className={classes.comparisonCard}>
							<Grid container justifyContent="center" alignItems="flex-end" spacing={7}>
								<Grid item>
									<Typography variant="caption" className={classes.comparisonLabel}>DE</Typography>
									<div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
										<WhatsAppIcon style={{ color: colors.green[600], fontSize: 22, flexShrink: 0 }} />
										<Typography variant="body2" className={classes.comparisonItemLabel}>WhatsApp Web</Typography>
									</div>
								</Grid>
								<Grid item>
									<Typography variant="body1" style={{ color: colors.gray[400], fontWeight: 700 }}>→</Typography>
								</Grid>
								<Grid item>
									<Typography variant="caption" className={classes.comparisonLabel}>PARA</Typography>
									<div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
										<ShieldCheckIcon size={22} color={colors.green[600]} style={{ flexShrink: 0 }} />
										<Typography variant="body2" className={classes.comparisonItemLabel}>API Oficial (WABA)</Typography>
									</div>
								</Grid>
							</Grid>
						</div>
					</Grid>

					<Grid item>
						<iframe
							title="Tutorial de migração para API Oficial"
							src={videoLinks.wabaMigrationTutorial}
							width="100%"
							height="360"
							className={classes.videoIframe}
							allowFullScreen
						/>
					</Grid>
				</Grid>
			</DialogContent>

			<DialogActions>
				<Button className={classes.actionButton} onClick={handleClose}>
					Agora não
				</Button>
				<Button
					className={`${classes.actionButton} ${classes.pulseButton}`}
					variant="contained"
					color="primary"
					onClick={handleGoToForm}
				>
					Iniciar migração
				</Button>
			</DialogActions>
		</>
	)

	const renderFormStep = () => (
		<>
			<IconButton onClick={handleClose} className={classes.closeIcon}>
				<CloseIcon />
			</IconButton>
			<DialogTitle disableTypography>
				<Grid container direction="column" spacing={1}>
					<Grid item>
						<Typography variant="h6" className={classes.title}>
							Formulário de Migração WABA
						</Typography>
					</Grid>
					<Grid item>
						<Typography variant="body2" className={classes.subtitle}>
							Preencha seus dados abaixo para que nosso time entre em contato e conduza a migração da sua conta para a API Oficial.
						</Typography>
					</Grid>
				</Grid>
			</DialogTitle>

			<DialogContent>
				<Grid container direction="column">
					<Grid item className={classes.formField}>
						<TextField
							label="Nome"
							value={formData.name}
							onChange={(event) => setFormData({ ...formData, name: event.target.value })}
							fullWidth
							variant="outlined"
							error={Boolean(validation.name)}
							helperText={validation.name}
						/>
					</Grid>

					<Grid item className={classes.formField}>
						<TextField
							label="WhatsApp"
							value={formData.phone}
							onChange={(event) => setFormData({ ...formData, phone: event.target.value })}
							fullWidth
							variant="outlined"
							error={Boolean(validation.phone)}
							helperText={validation.phone}
						/>
					</Grid>

					<Grid item className={classes.formField}>
						<FormControl component="fieldset" fullWidth error={Boolean(validation.objective)}>
							<Typography variant="body2" className={classes.objectiveLabel}>
								Principal objetivo
							</Typography>
							<RadioGroup
								value={formData.objective}
								onChange={(event) => setFormData({ ...formData, objective: event.target.value })}
							>
								{objectiveOptions.map((option) => (
									<FormControlLabel
										key={option.value}
										value={option.value}
										control={<Radio color="primary" size="small" />}
										label={<Typography variant="body2">{option.label}</Typography>}
									/>
								))}
							</RadioGroup>
							{Boolean(validation.objective) && (
								<Typography variant="caption" color="error" style={{ marginTop: 2, display: "block" }}>
									{validation.objective}
								</Typography>
							)}
							<Collapse in={formData.objective === "outro"}>
								<TextField
									style={{ marginTop: 8 }}
									label="Descreva seu objetivo"
									value={formData.objectiveOther}
									onChange={(event) => setFormData({ ...formData, objectiveOther: event.target.value })}
									fullWidth
									variant="outlined"
									multiline
									rows={3}
									error={Boolean(validation.objectiveOther)}
									helperText={validation.objectiveOther}
								/>
							</Collapse>
						</FormControl>
					</Grid>
				</Grid>
			</DialogContent>

			<DialogActions>
				<Button className={classes.actionButton} onClick={handleGoBack}>
					Voltar
				</Button>
				<Button
					className={classes.actionButton}
					variant="contained"
					color="primary"
					onClick={handleSubmit}
					disabled={loading}
					endIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
				>
					Concluir
				</Button>
			</DialogActions>
		</>
	)

	const renderSuccessStep = () => (
		<>
			<IconButton onClick={handleClose} className={classes.closeIcon}>
				<CloseIcon />
			</IconButton>

			<DialogContent>
				<Grid container direction="column" alignItems="center" spacing={2} style={{ paddingTop: 24, paddingBottom: 24 }}>
					<Grid item>
						<div className={classes.successIconWrapper}>
							<CheckIcon size={32} color={colors.green[600]} />
						</div>
					</Grid>

					<Grid item>
						<Typography variant="h3" align="center" className={classes.title}>
							Migração iniciada!
						</Typography>
					</Grid>

					<Grid item>
						<Typography variant="body2" align="center">
							Nosso time vai conduzir a evolução da sua operação para a API Oficial. Faremos contato com você através do número do responsável preenchido no formulário.
						</Typography>
					</Grid>

					<Grid item>
						<Button
							variant="contained"
							color="primary"
							onClick={handleClose}
						>
							Entendi
						</Button>
					</Grid>
				</Grid>
			</DialogContent>
		</>
	)

	const stepToRenderer: Record<Step, () => JSX.Element> = {
		info: renderInfoStep,
		form: renderFormStep,
		success: renderSuccessStep
	}

	return (
		<ThemeProvider theme={theme}>
			<Dialog
				open={opened}
				onClose={handleClose}
				disableBackdropClick
				maxWidth={false}
				fullWidth
				PaperProps={{ className: classes.dialogPaper }}
			>
				{stepToRenderer[step]()}
			</Dialog>
		</ThemeProvider>
	)
}

const WABAMigrationModal: WABAMigrationModalType = WABAMigrationModalContent as WABAMigrationModalType

WABAMigrationModal.open = ({ initialName, initialPhone }: WABAMigrationModalProps) => {
	renderComponent(MODAL_ID, <WABAMigrationModalContent initialName={initialName} initialPhone={initialPhone} />)
}

export default WABAMigrationModal
